import React, { useEffect } from "react";
import "./PrivacyPolicy.css";
import { useTranslation, Trans } from "react-i18next";

const APP_NAME = "OPENlift";
const DEVELOPER_NAME = "UniTech Engineering";
const COMPANY_ADDRESS = "60 Rue François 1er, 75008 Paris, France";
const SUPPORT_EMAIL = "contact@unitech-engineering.fr";
const OSM_PRIVACY_URL = "https://osmfoundation.org/wiki/Privacy_Policy";
const CNIL_URL = "https://www.cnil.fr";

export default function PrivacyPolicy() {
  const { t } = useTranslation();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t("privacyPolicy.seo.title", { appName: APP_NAME });

    let meta = document.querySelector('meta[name="description"]');
    const createdMeta = !meta;

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    const previousDescription = meta.getAttribute("content") || "";
    meta.setAttribute(
      "content",
      t("privacyPolicy.seo.description", {
        appName: APP_NAME,
        developerName: DEVELOPER_NAME,
      })
    );

    return () => {
      document.title = previousTitle;

      if (createdMeta) {
        meta.remove();
      } else {
        meta.setAttribute("content", previousDescription);
      }
    };
  }, [t]);

  const summaryCards = [
    {
      title: t("privacyPolicy.summary.cards.account.title"),
      text: t("privacyPolicy.summary.cards.account.text"),
    },
    {
      title: t("privacyPolicy.summary.cards.security.title"),
      text: t("privacyPolicy.summary.cards.security.text"),
    },
    {
      title: t("privacyPolicy.summary.cards.ads.title"),
      text: t("privacyPolicy.summary.cards.ads.text"),
    },
  ];

  const accountData = t("privacyPolicy.data.account.items", {
    returnObjects: true,
  });
  const authData = t("privacyPolicy.data.auth.items", {
    returnObjects: true,
  });
  const technicalData = t("privacyPolicy.data.technical.items", {
    returnObjects: true,
  });
  const elevatorData = t("privacyPolicy.data.elevator.items", {
    returnObjects: true,
  });
  const purposes = t("privacyPolicy.purposes.items", {
    returnObjects: true,
  });
  const retentionItems = t("privacyPolicy.retention.items", {
    returnObjects: true,
  });
  const rights = t("privacyPolicy.rights.items", {
    returnObjects: true,
  });

  return (
    <main
      className="page page-privacy-policy"
      aria-labelledby="privacy-policy-title"
    >
      {/* ===== Hero ===== */}
      <section className="privacy-policy-hero">
        <div className="privacy-policy-container">
          <header className="privacy-policy-hero-header">
            <p className="privacy-policy-eyebrow">
              {APP_NAME} • {DEVELOPER_NAME}
            </p>

            <h1 id="privacy-policy-title" className="privacy-policy-title">
              {t("privacyPolicy.hero.title")}
            </h1>

            <p className="privacy-policy-subtitle">
              <Trans
                i18nKey="privacyPolicy.hero.subtitle_html"
                values={{
                  appName: APP_NAME,
                  developerName: DEVELOPER_NAME,
                }}
                components={{
                  strong: <strong />,
                }}
              />
            </p>

            <p className="privacy-policy-updated">
              {t("privacyPolicy.hero.lastUpdated")}
            </p>
          </header>
        </div>
      </section>

      {/* ===== Résumé ===== */}
      <section
        className="privacy-policy-summary"
        aria-label={t("privacyPolicy.summary.aria")}
      >
        <div className="privacy-policy-container">
          <div className="privacy-policy-summary-grid">
            {summaryCards.map((card) => (
              <article className="privacy-summary-card" key={card.title}>
                <h2 className="privacy-summary-card-title">{card.title}</h2>
                <p className="privacy-summary-card-text">{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Contenu ===== */}
      <section className="privacy-policy-content">
        <div className="privacy-policy-container">
          <div className="privacy-policy-layout">
            {/* Sommaire */}
            <aside
              className="privacy-policy-toc"
              aria-label={t("privacyPolicy.toc.aria")}
            >
              <div className="privacy-policy-toc-inner">
                <p className="privacy-policy-toc-title">
                  {t("privacyPolicy.toc.title")}
                </p>

                <a href="#controller">{t("privacyPolicy.toc.controller")}</a>
                <a href="#scope">{t("privacyPolicy.toc.scope")}</a>
                <a href="#data">{t("privacyPolicy.toc.data")}</a>
                <a href="#purposes">{t("privacyPolicy.toc.purposes")}</a>
                <a href="#legal-bases">{t("privacyPolicy.toc.legalBases")}</a>
                <a href="#recipients">{t("privacyPolicy.toc.recipients")}</a>
                <a href="#retention">{t("privacyPolicy.toc.retention")}</a>
                <a href="#security">{t("privacyPolicy.toc.security")}</a>
                <a href="#rights">{t("privacyPolicy.toc.rights")}</a>
                <a href="#transfers">{t("privacyPolicy.toc.transfers")}</a>
                <a href="#children">{t("privacyPolicy.toc.children")}</a>
                <a href="#changes">{t("privacyPolicy.toc.changes")}</a>
                <a href="#contact">{t("privacyPolicy.toc.contact")}</a>
              </div>
            </aside>

            {/* Politique */}
            <div className="privacy-policy-sections">
              <article id="controller" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.controller.title")}</h2>
                <p>
                  <Trans
                    i18nKey="privacyPolicy.controller.text_html"
                    values={{
                      developerName: DEVELOPER_NAME,
                      companyAddress: COMPANY_ADDRESS,
                    }}
                    components={{
                      strong: <strong />,
                    }}
                  />
                </p>
                <p>
                  <Trans
                    i18nKey="privacyPolicy.controller.contact_html"
                    values={{ supportEmail: SUPPORT_EMAIL }}
                    components={{
                      emailLink: (
                        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                      ),
                    }}
                  />
                </p>
              </article>

              <article id="scope" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.scope.title")}</h2>
                <p>
                  <Trans
                    i18nKey="privacyPolicy.scope.text_html"
                    values={{ appName: APP_NAME }}
                    components={{
                      strong: <strong />,
                    }}
                  />
                </p>
                <div className="privacy-policy-note">
                  {t("privacyPolicy.scope.accountNote", { appName: APP_NAME })}
                </div>
              </article>

              <article id="data" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.data.title")}</h2>
                <p>{t("privacyPolicy.data.intro")}</p>

                <div className="privacy-data-grid">
                  <section className="privacy-data-block">
                    <h3>{t("privacyPolicy.data.account.title")}</h3>
                    <ul>
                      {accountData.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </section>

                  <section className="privacy-data-block">
                    <h3>{t("privacyPolicy.data.auth.title")}</h3>
                    <ul>
                      {authData.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </section>

                  <section className="privacy-data-block">
                    <h3>{t("privacyPolicy.data.technical.title")}</h3>
                    <ul>
                      {technicalData.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </section>

                  <section className="privacy-data-block">
                    <h3>{t("privacyPolicy.data.elevator.title")}</h3>
                    <ul>
                      {elevatorData.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </section>
                </div>

                <div className="privacy-policy-note privacy-policy-note-strong">
                  {t("privacyPolicy.data.noSensitivePermissions", {
                    appName: APP_NAME,
                  })}
                </div>
              </article>

              <article id="purposes" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.purposes.title")}</h2>
                <p>{t("privacyPolicy.purposes.intro")}</p>
                <ul className="privacy-policy-list">
                  {purposes.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </article>

              <article id="legal-bases" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.legalBases.title")}</h2>
                <p>{t("privacyPolicy.legalBases.text")}</p>
              </article>

              <article id="recipients" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.recipients.title")}</h2>
                <p>{t("privacyPolicy.recipients.internal")}</p>
                <p>{t("privacyPolicy.recipients.providers")}</p>

                <div className="privacy-policy-third-party">
                  <h3>{t("privacyPolicy.recipients.osm.title")}</h3>
                  <p>
                    <Trans
                      i18nKey="privacyPolicy.recipients.osm.text_html"
                      components={{
                        osmLink: (
                          <a
                            href={OSM_PRIVACY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            OpenStreetMap
                          </a>
                        ),
                      }}
                    />
                  </p>
                </div>

                <div className="privacy-policy-note privacy-policy-note-strong">
                  {t("privacyPolicy.recipients.noSale")}
                </div>
              </article>

              <article id="retention" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.retention.title")}</h2>
                <p>{t("privacyPolicy.retention.intro")}</p>

                <div className="privacy-retention-grid">
                  {retentionItems.map((item, index) => (
                    <section className="privacy-retention-item" key={index}>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <span>{item.duration}</span>
                    </section>
                  ))}
                </div>
              </article>

              <article id="security" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.security.title")}</h2>
                <p>{t("privacyPolicy.security.text")}</p>
                <ul className="privacy-policy-list">
                  <li>{t("privacyPolicy.security.items.encrypted")}</li>
                  <li>{t("privacyPolicy.security.items.secureStorage")}</li>
                  <li>{t("privacyPolicy.security.items.roles")}</li>
                  <li>{t("privacyPolicy.security.items.monitoring")}</li>
                </ul>
                <p className="privacy-policy-small">
                  {t("privacyPolicy.security.disclaimer")}
                </p>
              </article>

              <article id="rights" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.rights.title")}</h2>
                <p>{t("privacyPolicy.rights.intro")}</p>
                <ul className="privacy-policy-list">
                  {rights.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>

                <p>
                  <Trans
                    i18nKey="privacyPolicy.rights.request_html"
                    values={{ supportEmail: SUPPORT_EMAIL }}
                    components={{
                      emailLink: (
                        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                      ),
                    }}
                  />
                </p>

                <div className="privacy-policy-note">
                  <Trans
                    i18nKey="privacyPolicy.rights.delete_html"
                    components={{
                      deleteLink: (
                        <a
                          href="/delete-account"
                          aria-label={t(
                            "privacyPolicy.rights.deleteLinkAria"
                          )}
                        >
                          {t("privacyPolicy.rights.deleteLinkLabel")}
                        </a>
                      ),
                    }}
                  />
                </div>

                <p>
                  <Trans
                    i18nKey="privacyPolicy.rights.cnil_html"
                    components={{
                      cnilLink: (
                        <a
                          href={CNIL_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          CNIL
                        </a>
                      ),
                    }}
                  />
                </p>
              </article>

              <article id="transfers" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.transfers.title")}</h2>
                <p>{t("privacyPolicy.transfers.text")}</p>
              </article>

              <article id="children" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.children.title")}</h2>
                <p>{t("privacyPolicy.children.text", { appName: APP_NAME })}</p>
              </article>

              <article id="changes" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.changes.title")}</h2>
                <p>{t("privacyPolicy.changes.text")}</p>
              </article>

              <article id="contact" className="privacy-policy-section-card">
                <h2>{t("privacyPolicy.contact.title")}</h2>
                <p className="privacy-policy-contact-block">
                  <strong>{DEVELOPER_NAME}</strong>
                  <br />
                  {COMPANY_ADDRESS}
                  <br />
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
